import { useEffect, useState } from 'react';
import { Target, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Opt-in Vision Deck retargeting settings.
 *
 * Data model (server-side only — spec: "Store nothing sensitive in
 * frontend"):
 *   users.retargeting_settings = {
 *     enabled: bool,
 *     pixel_id: string,
 *     access_token: string   // full value NEVER round-trips to frontend
 *   }
 * The GET endpoint returns only `access_token_last4` + `has_access_token`.
 */
export default function RetargetingSettingsCard() {
  const [settings, setSettings] = useState(null);
  const [pixelId, setPixelId] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const token = localStorage.getItem('token');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/settings/retargeting`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        setSettings(data);
        setPixelId(data.pixel_id || '');
        setEnabled(!!data.enabled);
      } catch {
        /* silent */
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/settings/retargeting`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          enabled,
          pixel_id: pixelId || null,
          // Only send the token field if the user typed a new one;
          // undefined leaves the existing server-side value in place.
          ...(accessToken ? { access_token: accessToken } : {}),
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data?.detail?.message || data?.detail || 'Could not save retargeting settings');
        return;
      }
      setSettings(data);
      setAccessToken('');
      toast.success('Retargeting settings saved');
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const testCapi = async () => {
    setTesting(true);
    try {
      const res = await fetch(`${API}/api/retargeting/facebook-capi`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          deck_id: 'test-connection-ping',
          event_name: 'ViewContent',
        }),
      });
      const data = await res.json();
      if (res.ok && data?.success) {
        toast.success('CAPI test succeeded — check your Meta Events Manager');
      } else {
        toast.error(data?.detail?.message || data?.error || 'CAPI test failed');
      }
    } catch {
      toast.error('CAPI test failed');
    } finally {
      setTesting(false);
    }
  };

  return (
    <Card className="rounded-sm border" data-testid="retargeting-settings-card">
      <CardHeader>
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Target className="h-4 w-4" />
          Vision Deck Retargeting
          <span className="text-[10px] uppercase tracking-wider text-amber-700 bg-amber-100 px-2 py-0.5 rounded ml-auto">
            Professional+
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-sm">Enable Vision Deck retargeting</p>
            <p className="text-xs text-muted-foreground">
              Fire a Facebook CAPI ViewContent event when a recipient opens your Vision Deck,
              then a follow-up "DeckRetarget2h" event 2 hours later.
            </p>
          </div>
          <Switch
            checked={enabled}
            onCheckedChange={setEnabled}
            data-testid="retargeting-enabled-toggle"
          />
        </div>
        <Separator />
        <div className="space-y-2">
          <Label htmlFor="pixel-id" className="text-xs uppercase tracking-wider">Facebook Pixel ID</Label>
          <Input
            id="pixel-id"
            placeholder="1234567890123456"
            value={pixelId}
            onChange={(e) => setPixelId(e.target.value)}
            data-testid="retargeting-pixel-id-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="access-token" className="text-xs uppercase tracking-wider">
            Facebook Conversions API Access Token
          </Label>
          <Input
            id="access-token"
            type="password"
            placeholder={settings?.has_access_token ? `•••• saved (ends …${settings.access_token_last4})` : 'EAAG...'}
            value={accessToken}
            onChange={(e) => setAccessToken(e.target.value)}
            data-testid="retargeting-access-token-input"
          />
          <p className="text-[11px] text-muted-foreground">
            Stored server-side only. Never round-tripped to the browser.
          </p>
        </div>
        <div className="flex gap-2 pt-2">
          <Button
            onClick={save}
            disabled={saving}
            className="flex-1"
            data-testid="retargeting-save-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
          </Button>
          <Button
            variant="outline"
            onClick={testCapi}
            disabled={testing || !enabled || !settings?.has_access_token && !accessToken}
            data-testid="retargeting-test-btn"
          >
            {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Test CAPI'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
